import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import GameServices from "../services/GameServices";
import { getStoredActiveGameTab, setStoredActiveGameTab } from "../utils/utils";

/**
 * The running games, and which one a screen is currently showing.
 *
 * Every screen that used to hardcode the pair 3min / 5min builds its tabs
 * from this instead. It also keeps the selection honest: if the game a
 * user was last looking at has since been stopped, the selection moves to
 * the first game that is still running rather than leaving the screen
 * asking the server for a game that is no longer there.
 *
 * @param {object}  options
 * @param {boolean} options.remember  persist the choice across navigation.
 */
export default function useActiveGames({ remember = true } = {}) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCode, setActiveCode] = useState(() =>
    remember ? getStoredActiveGameTab() : "",
  );

  const load = useCallback(async () => {
    const api = new GameServices();
    try {
      const res = await api.getActiveGames();
      const list = res?.isSuccess ? res.data || [] : [];
      setGames(list);

      setActiveCode((current) => {
        if (list.some((g) => g.game_code === current)) return current;
        return list[0]?.game_code || "";
      });
      return list;
    } catch (err) {
      console.error("Error loading active games:", err);
      toast.error("Could not load the game list");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (remember && activeCode) setStoredActiveGameTab(activeCode);
  }, [remember, activeCode]);

  const activeGame = useMemo(
    () => games.find((g) => g.game_code === activeCode) || null,
    [games, activeCode],
  );

  return {
    games,
    loading,
    activeCode,
    setActiveCode,
    activeGame,
    /** Board size of the selected game; drives the yantra grid. */
    totalGhar: Number(activeGame?.total_ghar) || 0,
    reload: load,
  };
}
